import colors from '@mathflat/common/style/colors'
import clsx from 'clsx'
import { isSameDay, isSameMonth, isToday, lastDayOfMonth } from 'date-fns'
import { isSunday } from 'date-fns/esm'
import { range } from 'lodash-es'
import { runInAction } from 'mobx'
import { observer, useLocalObservable } from 'mobx-react'
import React, { useEffect, useRef, useState } from 'react'

import { Icon } from '@/component/atoms/Icon'
import GroupArrow from '@/component/atoms/motionIcon/GroupArrow'
import MultiSelectContents from '@/component/molecules/MultiSelectDropdown/MultiSelectContents'
import { CALENDAR_COMPONENT } from '@/constant/ui/DOM'
import useEffectAfterMount from '@/hooks/useEffectAfterMount'
import type { StyleProps } from '@/types'
import type { DateStr } from '@/types/common/commonTypes'
import dateUtils from '@/utils/DateUtils'

import S from './Calendar.style'

type _BaseProps = {
  selectedDate?: Date
  isSundayRed?: boolean
  maxDate?: Date
  minDate?: Date
  disabled?: boolean
  pointedDates?: Set<DateStr<'-'>>
  pointSize?: 'small' | 'big'
  multiSelectYear?: number[]
  onPrevMonthClick?: ({ year, month }: { year: number; month: number }) => void
  onNextMonthClick?: ({ year, month }: { year: number; month: number }) => void
  onClickOutside?: () => void
  footer?: React.ReactNode
  scrollIntoView?: boolean
  checkDisabled?: (date: Date) => boolean
} & StyleProps

type _DailyWeeklyProps = _BaseProps & {
  type?: 'daily' | 'weekly'
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  weeklyItem?: (week: any, i: number) => React.ReactNode
  onDateSelect: (date: Date) => void
  startDate?: never
  endDate?: never
  onDateRangeSelect?: never
}

type _RangeProps = _BaseProps & {
  type: 'range'
  weeklyItem?: never
  onDateSelect?: never
  startDate?: Date
  endDate?: Date
  onDateRangeSelect: (startDate: Date, endDate: Date | null) => void
}

const Calendar = ({
  className,
  style,
  onDateSelect,
  selectedDate,
  isSundayRed = false,
  maxDate,
  minDate,
  disabled = false,
  pointedDates,
  multiSelectYear = range(2011, new Date().getFullYear() + 2),
  pointSize = 'small',
  onPrevMonthClick,
  onNextMonthClick,
  onClickOutside,
  footer,
  scrollIntoView = false,
  type = 'daily',
  weeklyItem,
  checkDisabled,
  startDate,
  endDate,
  onDateRangeSelect,
}: _DailyWeeklyProps | _RangeProps) => {
  const store = useLocalObservable(() => ({
    year: (selectedDate ?? new Date()).getFullYear(),
    month: (selectedDate ?? new Date()).getMonth() + 1,
    day: (selectedDate ?? new Date()).getDate(),
    get monthArr() {
      return this.renderCalendar()
    },
    renderCalendar() {
      const dayOfPrevMonth = lastDayOfMonth(new Date(store.year, store.month - 2)).getDay()
      const timeOfPrevMonth = lastDayOfMonth(new Date(store.year, store.month - 2)).getTime()
      const timeOfCurrentMonth = lastDayOfMonth(new Date(store.year, store.month - 1)).getTime()
      const dateOfCurrentMonth = new Date(store.year, store.month, 0).getDate()

      // 지난 달 추가
      const dateArr =
        dayOfPrevMonth === 6
          ? []
          : Array.from({ length: dayOfPrevMonth + 1 }, (_, i) => ({
              date: new Date(timeOfPrevMonth - 86400000 * i),
              isDateOfCurrentMonth: false,
            })).reverse()

      // 이번 달 추가
      range(dateOfCurrentMonth).forEach((_, i) =>
        dateArr.push({
          date: new Date(store.year, store.month - 1, i + 1),
          isDateOfCurrentMonth: true,
        }),
      )

      // 다음 달 추가
      range(42 - dateArr.length).forEach((_, i) => {
        dateArr.push({
          date: new Date(timeOfCurrentMonth + 86400000 * (i + 1)),
          isDateOfCurrentMonth: false,
        })
      })

      return dateArr
    },

    increaseMonth: () => {
      const nextYear = new Date().getFullYear() + 1
      const maximumMonth = new Date().getMonth() + 1

      if (store.year === nextYear && store.month === maximumMonth) {
        return
      }

      if (store.month === 12) {
        store.month = 1
        store.year = store.year + 1
      } else {
        store.month = store.month + 1
      }
    },

    decreaseMonth: () => {
      if (store.month === 1) {
        store.month = 12
        store.year = store.year - 1
      } else {
        store.month = store.month - 1
      }
    },
  }))

  const [openedMultiSelectDropdown, setOpenedMultiSelectDropdown] = useState(false)
  const selectedData = { year: store.year, month: store.month }

  const containerRef = useRef<HTMLDivElement>(null)

  const _onClickOutside = (e: MouseEvent) => {
    if (!containerRef.current?.contains(e.target as Node)) {
      onClickOutside?.()
    }
  }

  useEffect(() => {
    if (scrollIntoView) {
      containerRef.current?.scrollIntoView()
    }

    window.addEventListener('click', _onClickOutside)
    return () => {
      window.removeEventListener('click', _onClickOutside)
    }
  }, [])

  useEffectAfterMount(() => {
    runInAction(() => {
      store.month = (selectedDate ?? new Date()).getMonth() + 1
      store.year = (selectedDate ?? new Date()).getFullYear()
      store.day = (selectedDate ?? new Date()).getDate()
    })
  }, [selectedDate])

  const weeklyArr = dateUtils.getWeeklySundays(
    dateUtils.getSameWeekThursday(new Date(store.year, store.month - 1, store.day)),
  )

  const handleDateClick = (date: Date) => {
    if (type === 'range' && onDateRangeSelect) {
      if (!startDate) {
        onDateRangeSelect(date, null)
      } else if (startDate && !endDate) {
        if (date < startDate) {
          onDateRangeSelect(date, startDate)
        } else if (date > startDate) {
          onDateRangeSelect(startDate, date)
        } else {
          onDateRangeSelect(startDate, date)
        }
      } else if (startDate && endDate) {
        onDateRangeSelect(date, null)
      }
    } else {
      onDateSelect?.(date)
    }
  }

  const isDateInRange = (date: Date) => {
    if (!startDate || !endDate) return false
    return date >= startDate && date <= endDate
  }

  const isRangeStart = (date: Date) => {
    if (!startDate) return false
    return isSameDay(date, startDate)
  }

  const isRangeEnd = (date: Date) => {
    if (!endDate) return false
    return isSameDay(date, endDate)
  }

  const hasValidRange = startDate && endDate && !isSameDay(startDate, endDate)

  const renderCalendarBody = () => {
    if (type === 'daily' || type === 'range') {
      return (
        <div className="calendar-body">
          <div className="calendar-date">일</div>
          <div className="calendar-date">월</div>
          <div className="calendar-date">화</div>
          <div className="calendar-date">수</div>
          <div className="calendar-date">목</div>
          <div className="calendar-date">금</div>
          <div className="calendar-date">토</div>
          {store.monthArr.map(({ date, isDateOfCurrentMonth }) => {
            const isDisable = (() => {
              if (!isDateOfCurrentMonth || disabled) return true
              if (maxDate && maxDate < date) return true
              if (minDate && minDate > date) return true
            })()

            return (
              <button
                key={date.getTime()}
                data-mathflat-unique-id={date.getTime()}
                disabled={isDisable}
                onClick={(e) => {
                  e.stopPropagation()
                  if (isDateOfCurrentMonth) {
                    handleDateClick(date)
                  }
                }}
                className={clsx(
                  'calendar-day',
                  isToday(date) && 'today',
                  type !== 'range' && selectedDate && isSameDay(date, selectedDate) && 'selected',
                  type === 'range' && isRangeStart(date) && 'range-start',
                  type === 'range' && isRangeEnd(date) && 'range-end',
                  type === 'range' && isDateInRange(date) && 'in-range',
                  type === 'range' && hasValidRange && 'has-range',
                  pointedDates?.has(dateUtils.toDateString(date, '-')) && `pointed ${pointSize}`,
                  isSameMonth(date, new Date()) ? 'in-month' : 'out-month',
                  isSundayRed &&
                    isSameMonth(date, new Date(`${store.year}-${store.month}-01`)) &&
                    isSunday(date) &&
                    'sunday',
                )}
              >
                <span>{date.getDate()}</span>
              </button>
            )
          })}
        </div>
      )
    }
    if (type === 'weekly') {
      return (
        <div className="calendar-weekly-list">
          {weeklyArr.map((week, i) => {
            const isSelectedWeek = isSameDay(selectedDate!, new Date(week))

            const disabled = checkDisabled ? checkDisabled(new Date(week)) : false

            return (
              <div
                key={week}
                className={clsx(
                  'calendar-weekly-item',
                  isSelectedWeek && 'selected-week-item',
                  disabled && 'disabled',
                )}
                aria-disabled={disabled || undefined}
                data-testid="calendar-weekly-item"
                onClick={(e) => {
                  e.stopPropagation()
                  if (!disabled) {
                    onDateSelect?.(new Date(week))
                  }
                }}
              >
                {weeklyItem && weeklyItem(week, i)}
              </div>
            )
          })}
          <p className="weekly-guide">
            매월 첫 목요일이 포함된 주가 해당 월의 1주차입니다. (국제 표준)
          </p>
        </div>
      )
    }
  }

  return (
    <div
      css={S.default}
      className={clsx(CALENDAR_COMPONENT, className)}
      style={style}
      onClick={(e) => {
        e.stopPropagation()
      }}
      ref={containerRef}
    >
      <div className="calendar-container">
        <div className="calendar-header">
          <button
            className="calendar-move-month-prev"
            onClick={(e) => {
              e.stopPropagation()
              store.decreaseMonth()
              onPrevMonthClick?.({ year: store.year, month: store.month - 1 })
            }}
          >
            <Icon name="navigation_arrow_back" size={14} color="white" css={{ marginRight: 4 }} />
            <p>{store.month === 1 ? 12 : store.month - 1}월</p>
          </button>
          <button
            className="multi-select-dropdown-button"
            onClick={() => setOpenedMultiSelectDropdown((prev) => !prev)}
          >
            {store.year}년 {store.month}월
            <div className="group-arrow-box">
              <GroupArrow
                isOpen={openedMultiSelectDropdown}
                startDeg={180}
                endDeg={0}
                color={colors.blue.$300}
                openedColor={colors.blue.$300}
              />
            </div>
          </button>
          <button
            className="calendar-move-month-next"
            onClick={(e) => {
              e.stopPropagation()
              store.increaseMonth()
              onNextMonthClick?.({ year: store.year, month: store.month - 1 })
            }}
          >
            <p>{store.month === 12 ? 1 : store.month + 1}월</p>
            <Icon name="navigation_arrow_right" size={14} color="white" css={{ marginLeft: 4 }} />
          </button>
        </div>
        <div className={clsx('calendar-body-container', footer && 'has-footer')}>
          {openedMultiSelectDropdown ? (
            <MultiSelectContents
              width="100%"
              data={[
                ['year', multiSelectYear],
                ['month', range(1, 13)],
              ]}
              selectedData={selectedData}
              label={{
                year: '년',
                month: '월',
              }}
              onSelect={(name, value) => {
                runInAction(() => {
                  store[name] = value
                })

                if (name === 'month') {
                  setTimeout(() => {
                    setOpenedMultiSelectDropdown(false)
                  }, 0)
                }
              }}
            />
          ) : (
            renderCalendarBody()
          )}
        </div>
        {footer}
      </div>
    </div>
  )
}

export default observer(Calendar)
